import useServerPagination,{ServerPageStatus} from '../../components/ui/useServerPagination';
import PermissionButton from "../../components/ui/PermissionButton";
import StaffDepartmentAccess from './StaffDepartmentAccess';
import TableActions from '../../components/ui/TableActions';
import { useState, useEffect } from "react";
import { Search, Users, UserCheck, Building2, ShieldCheck } from "lucide-react";
import DashboardLayout from "../Layouts/DashboardLayout";
import { useAuth } from "../Context/AuthContext";
import PeopleExcelImport from '../Components/PeopleExcelImport';

export default function UserPermissions() {
  const { user: loggedInUser, branchId } = useAuth();
  const [departmentsRaw,setDepartmentsRaw]=useState([]);
  useEffect(()=>{fetch('/api/departments',{credentials:'include'}).then(r=>r.json()).then(data=>{if(data.status==='success')setDepartmentsRaw(data.data);}).catch(()=>{});},[]);
  const canSetBranchAccess = loggedInUser?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [roleList, setRoleList] = useState([]);
  const [branches, setBranches] = useState([]);
  const [counters, setCounters] = useState([]);
  const [permissionsList, setPermissionsList] = useState([]);

  const [rolesRaw, setRolesRaw] = useState([]);
  const [branchesRaw, setBranchesRaw] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [accessFilter, setAccessFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const server=useServerPagination({endpoint:'/staff',onData:setUsers,search,filters:{status:statusFilter,access:accessFilter},page,pageSize,onPageChange:setPage});
  const pageCount = server.pagination.pages;
  const currentPage = server.pagination.page;
  const visibleUsers = users;
  useEffect(() => setPage(1), [search,statusFilter,accessFilter,pageSize]);

  const [form, setForm] = useState({
    name: "",
    email: "",
    employee_id: "",
    password: "",
    role_id: "",
    branch_id: "", // ✅ ONLY THIS (no branch name)
    branch_access: "assigned",
    branch_access_ids: [],
    department_id: "", department_access: "assigned", department_access_ids: [],
    counter_id: "",
    status: "Active",
    permissions: [],
    compensation: {
      salaryEnabled: false,
      salaryAmount: "",
      commissionEnabled: false,
      commissionType: "percentage",
      commissionValue: "",
    },
  });

  const [newRole, setNewRole] = useState({
    name: "",
    description: "",
    permissions: [],
  });

  const fetchCounters = async (branchId) => {
    if (!branchId) {
      setCounters([]);
      return;
    }

    try {
      const res = await fetch(
        `/api/counters/?branch_id=${branchId}`,
        {
          credentials: "include",
        },
      );

      const data = await res.json();

      if (data.status === "success") {
        setCounters(data.data.filter((counter) => counter.is_active));
      } else {
        setCounters([]);
      }
    } catch (error) {
      console.error(error);
      setCounters([]);
    }
  };

  // ================= FETCH =================
  useEffect(() => {
    const fetchData = async () => {
      const [rRes, bRes, pRes] = await Promise.all([
        fetch("/api/roles", {
          credentials: "include",
        }),
        fetch("/api/branches", {
          credentials: "include",
        }),
        fetch("/api/permissions", {
          credentials: "include",
        }),
      ]);

      const rData = await rRes.json();
      const bData = await bRes.json();
      const pData = await pRes.json();

      setRolesRaw(rData.data || []);
      setBranchesRaw(bData.data || []);
      setPermissionsList(pData.data || []);

      const roleNames = (rData.data || []).map((r) => r.name);
      const branchNames = (bData.data || []).map((b) => b.name);

      setRoleList(roleNames);
      setBranches(branchNames);

      setForm((prev) => ({
        ...prev,
        role: prev.role || roleNames[0] || "",
        branch: prev.branch || branchNames[0] || "",
      }));
    };

    fetchData();
  }, []);

  const togglePermission = (perm) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter((p) => p !== perm)
        : [...prev.permissions, perm],
    }));
  };

  const handleRoleChange = (roleId) => {
    const role = rolesRaw.find((r) => r.id === Number(roleId));

    if (!role) return;

    setForm((prev) => ({
      ...prev,
      role_id: role.id,
      permissions: role.permissions || [],
    }));
  };

  // ================= SAVE =================
  const saveUser = async () => {
    if (!form.name || !form.email || (!editingId && !form.password)) {
      alert("Fill required fields");
      return;
    }

    const role = rolesRaw.find((r) => r.id === form.role_id);

    if (!form.role_id || !form.branch_id) {
      alert("Role or Branch not selected");
      return;
    }

    const payload = {
      name: form.name,
      email: form.email,
      employee_id: form.employee_id,
      role_id: Number(form.role_id),
      branch_id: Number(form.branch_id), // ✅ DIRECT
      branch_access: form.branch_access || "assigned",
      branch_access_ids: form.branch_access_ids || [],
      department_id: form.department_id ? Number(form.department_id) : null, department_access: form.department_access || "assigned", department_access_ids: form.department_access_ids || [],
      counter_id: form.counter_id ? Number(form.counter_id) : null,
      status: form.status,
      permissions: form.permissions,
      compensation: {
        salaryEnabled: form.compensation.salaryEnabled,
        salaryAmount: form.compensation.salaryAmount
          ? Number(form.compensation.salaryAmount)
          : 0,
        commissionEnabled: form.compensation.commissionEnabled,
        commissionType: form.compensation.commissionType,
        commissionValue: form.compensation.commissionValue
          ? Number(form.compensation.commissionValue)
          : 0,
      },
    };

    if (form.password) {
      payload.password = form.password;
    }

    const url = editingId
      ? `/api/staff/${editingId}`
      : "/api/staff";

    const method = editingId ? "PUT" : "POST";

    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (data.status === "success") {
      server.reload();

      setShowModal(false);
      setEditingId(null);

      setForm({
        name: "",
        email: "",
        employee_id: "",
        password: "",
        role_id: rolesRaw[0]?.id || "",
        branch_id: branchesRaw[0]?.id || "",
        branch_access: "assigned",
        branch_access_ids: [],
    department_id: "", department_access: "assigned", department_access_ids: [],
        counter_id: "",
        status: "Active",
        permissions: [],
        compensation: {
          salaryEnabled: false,
          salaryAmount: "",
          commissionEnabled: false,
          commissionType: "percentage",
          commissionValue: "",
        },
      });
    } else {
      alert(data.detail || data.message || "Could not save staff");
    }
  };

  // ================= EDIT =================
  const handleEdit = (user) => {
    const role = rolesRaw.find((r) => r.id === user.role_id); // ✅ FIX

    const rolePerms = role?.permissions || [];

    const merged = [...new Set([...rolePerms, ...(user.permissions || [])])];

    setForm({
      name: user.name,
      email: user.email,
      employee_id: user.employee_id || "",
      password: "",
      role_id: role?.id, // ✅ source of truth
      branch_id: user.branch_id,
      branch_access: user.branch_access || "assigned",
      branch_access_ids: user.branch_access_ids || [],
      department_id: user.department_id || "", department_access: user.department_access || "assigned", department_access_ids: user.department_access_ids || [],
      counter_id: user.counter_id || "",
      status: user.status,
      permissions: merged,
      compensation: user.compensation || {
        salaryEnabled: false,
        salaryAmount: "",
        commissionEnabled: false,
        commissionType: "percentage",
        commissionValue: "",
      },
    });

    setEditingId(user.id);
    setShowModal(true);
    fetchCounters(user.branch_id);
  };

  const handleDisable = async (user) => {
    const newStatus = user.status === "Active" ? "Disabled" : "Active";

    const res = await fetch(`/api/staff/${user.id}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status: newStatus }),
    });

    const data = await res.json();

    if (data.status === "success") {
      server.reload();
    } else {
      alert("Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this user?",
    );
    if (!confirmDelete) return;

    const res = await fetch(`/api/staff/${id}`, {
      method: "DELETE",
      credentials: "include",
    });

    const data = await res.json();

    if (data.status === "success") {
      server.reload();
    } else {
      alert("Delete failed");
    }
  };

  const groupedPermissions = permissionsList.reduce((acc, perm) => {
    const module = perm.module?.trim() || "Other";

    if (!acc[module]) {
      acc[module] = [];
    }

    acc[module].push(perm);

    return acc;
  }, {});

  // sort permissions inside every module
  Object.keys(groupedPermissions).forEach((module) => {
    groupedPermissions[module].sort((a, b) =>
      (a.display_name || "").localeCompare(b.display_name || ""),
    );
  });

  // sort modules alphabetically
  const sortedGroupedPermissions = Object.fromEntries(
    Object.entries(groupedPermissions).sort(([a], [b]) => a.localeCompare(b)),
  );

  const isAllSelected = newRole.permissions.length === permissionsList.length;

  const isPartial =
    newRole.permissions.length > 0 &&
    newRole.permissions.length < permissionsList.length;

  return (
    <DashboardLayout>
      <div className="space-y-10">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div>
            <p className="erp-eyebrow">People & Access</p>
            <h1 className="text-2xl font-serif">Staff Management</h1>
            <p className="mt-2 text-sm text-gray-400">Manage employee profiles, roles, compensation, and branch access.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
          <PeopleExcelImport kind="staff" onImported={()=>{setPage(1);server.reload();}} />
          <PermissionButton permissionModule="staff"
            onClick={() => {
              const defaultRole = rolesRaw[0];
              const defaultBranch = loggedInUser?.role === "staff"
                ? branchesRaw.find((b) => Number(b.id) === Number(branchId)) : branchesRaw[0];

              setEditingId(null);

              setForm((prev) => ({
                ...prev,
                name: "", email: "", employee_id: "", password: "", status: "Active",
                compensation: { salaryEnabled: false, salaryAmount: "", commissionEnabled: false, commissionType: "percentage", commissionValue: "" },
                role_id: defaultRole?.id || "",
                branch_id: defaultBranch?.id || "",
                branch_access: "assigned",
                branch_access_ids: [],
    department_id: "", department_access: "assigned", department_access_ids: [],
                counter_id: "",
                permissions: defaultRole?.permissions || [],
              }));

              if (defaultBranch?.id) {
                fetchCounters(defaultBranch.id);
              }

              setShowModal(true);
            }}
            className="px-5 py-2 rounded-xl bg-amber-400 text-black font-medium"
          >
            + Add Staff
          </PermissionButton>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { label:"Matching staff", value:server.pagination.total, icon:Users },
            { label:"Active employees", value:server.summary.active||0, icon:UserCheck },
            { label:"Home branches", value:new Set(users.map((employee) => employee.branch_id).filter(Boolean)).size, icon:Building2 },
            { label:"Cross-branch access", value:Number(server.summary.all_access||0)+Number(server.summary.selected_access||0), icon:ShieldCheck },
          ].map(({label,value,icon:Icon}) => <div key={label} className="erp-stat-card">
            <div><p>{label}</p><strong>{value}</strong></div><span><Icon size={20} /></span>
          </div>)}
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
          <div className="erp-table-toolbar">
            <div className="erp-search-control"><Search size={16} aria-hidden="true" />
              <input aria-label="Search staff" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, employee code, role, or branch..." />
            </div>
            <select aria-label="Filter staff status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option><option value="Active">Active</option><option value="Disabled">Disabled</option>
            </select>
            <select aria-label="Filter branch access" value={accessFilter} onChange={(e) => setAccessFilter(e.target.value)}>
              <option value="">All branch access</option><option value="assigned">Assigned branch</option><option value="selected">Selected branches</option><option value="all">All branches</option>
            </select>
          </div>
          <div className="overflow-x-auto">
          <><ServerPageStatus pagination={server.pagination}/><table className="w-full text-sm">
            <thead className="bg-black/40 text-gray-400">
              <tr>
                <th className="px-6 py-4 text-left">Employee ID</th>
                <th className="px-6 py-4 text-left">Name</th>
                <th className="px-6 py-4">Email</th>
                <th className="px-6 py-4">Role</th>
                <th className="px-6 py-4">Branch</th>
                <th className="px-6 py-4">Compensation</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.length === 0 && <tr><td colSpan={8} className="text-center">No staff match your filters.</td></tr>}
              {visibleUsers.map((u) => (
                <tr
                  key={u.id}
                  className="border-t border-white/10 hover:bg-white/5"
                >
                  <td className="px-6 py-4 font-medium">{u.employee_id}</td>
                  <td className="px-6 py-4 font-medium">{u.name}</td>
                  <td className="px-6 py-4 text-center">{u.email}</td>
                  <td className="px-6 py-4 text-center">{u.role}</td>
                  <td className="px-6 py-4 text-center">
                    <div>{u.branch}</div>
                    <div className="mt-1 text-xs text-amber-400">
                      {u.branch_access === "all" ? "All branches" : u.branch_access === "selected"
                        ? `Selected branches (${(u.branch_access_ids || []).length})` : "Assigned branch only"}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center text-xs">
                    {u.compensation?.salaryEnabled && "Salary"}
                    {u.compensation?.salaryEnabled &&
                      u.compensation?.commissionEnabled &&
                      " + "}
                    {u.compensation?.commissionEnabled && "Commission"}
                  </td>

                  <td className="px-6 py-4 text-center">
                    <span
                      className={`px-3 py-1 rounded-full text-xs ${
                        u.status === "Active"
                          ? "bg-emerald-500/20 text-emerald-400"
                          : "bg-red-500/20 text-red-400"
                      }`}
                    >
                      {u.status}
                    </span>
                  </td>
                  <td className="px-6 py-4"><TableActions>
                    <div className="flex items-center gap-2">
                      <PermissionButton permissionModule="staff" permissionAction="edit"
                        onClick={() => handleEdit(u)}
                        className="
        px-3 py-1.5 text-xs rounded-lg
        border border-white/20
        text-gray-300
        hover:bg-white/10 hover:text-white
        transition
      "
                      >
                        Edit
                      </PermissionButton>
                      <PermissionButton permissionModule="staff"
                        onClick={() => handleDisable(u)}
                        className="
    px-3 py-1.5 text-xs rounded-lg
    border border-amber-400/40
    text-amber-400
    hover:bg-amber-400/10
    transition
  "
                      >
                        {u.status === "Active" ? "Disable" : "Enable"}
                      </PermissionButton>

                      <PermissionButton permissionModule="staff" permissionAction="delete"
                        onClick={() => handleDelete(u.id)}
                        className="
    px-3 py-1.5 text-xs rounded-lg
    border border-red-500/40
    text-red-400
    hover:bg-red-500/10
    transition
  "
                      >
                        Delete
                      </PermissionButton>
                    </div>
                  </TableActions></td>
                </tr>
              ))}
            </tbody>
          </table></>
          </div>
          <div className="erp-pagination">
            <span>{server.pagination.total ? `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize,server.pagination.total)}` : "0"} of {server.pagination.total} staff</span>
            <div><label>Rows <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Rows per page">
              {[10,25,50].map((size) => <option key={size} value={size}>{size}</option>)}</select></label>
              <PermissionButton permissionModule="staff" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} className="rounded-lg border border-white/10 px-3 py-2">Previous</PermissionButton>
              <span>{currentPage} / {pageCount}</span>
              <PermissionButton permissionModule="staff" disabled={currentPage >= pageCount} onClick={() => setPage(currentPage + 1)} className="rounded-lg border border-white/10 px-3 py-2">Next</PermissionButton>
            </div>
          </div>
        </div>

        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <div className="erp-modal-heading">
              <p className="erp-eyebrow">People & Access</p>
              <h2>{editingId ? "Edit staff member" : "Add staff member"}</h2>
              <p>Employee details, home branch, and role permissions stay together in one profile.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <input
                placeholder="Full Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              />

              <input
                placeholder="Email (Login ID)"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              />

              <input
                placeholder="Employee ID"
                value={form.employee_id}
                onChange={(e) =>
                  setForm({ ...form, employee_id: e.target.value })
                }
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              />

              <input
                type="password"
                placeholder={
                  editingId ? "Leave blank to keep same password" : "Password"
                }
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              />

              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
              >
                <option>Active</option>
                <option>Disabled</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="relative">
                <label className="text-xs text-gray-400 uppercase tracking-widest">
                  Role
                </label>

                <div className="flex gap-2 mt-1">
                  <select
                    value={form.role_id}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    className="flex-1 px-4 py-2 rounded-lg bg-black/40 border border-white/10"
                  >
                    {rolesRaw.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>

                  <PermissionButton permissionModule="staff"
                    type="button"
                    onClick={() => setShowRoleModal(true)}
                    className="px-4 rounded-lg bg-white/10 hover:bg-white/20 transition text-sm"
                  >
                    + Role
                  </PermissionButton>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase tracking-widest">
                  Home Branch
                </label>
                <select
                  value={form.branch_id}
                  disabled={loggedInUser?.role === "staff"}
                  onChange={(e) => {
                    const branchId = Number(e.target.value);

                    setForm({
                      ...form,
                      branch_id: branchId,
                      counter_id: "",
                    });

                    fetchCounters(branchId);
                  }}
                  className="mt-1 px-4 py-2 rounded-lg bg-black/40 border border-white/10 w-full"
                >
                  {branchesRaw.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>

              <StaffDepartmentAccess form={form} setForm={setForm} departments={departmentsRaw} canEdit={canSetBranchAccess} />
              <div className="md:col-span-2 rounded-xl border border-amber-400/20 bg-amber-400/5 p-4">
                <label className="text-xs uppercase tracking-widest text-amber-400">Branch Access</label>
                <select value={form.branch_access || "assigned"} disabled={!canSetBranchAccess}
                  onChange={(e) => setForm({ ...form, branch_access: e.target.value,
                    branch_access_ids: e.target.value === "selected" ? [...new Set([Number(form.branch_id), ...(form.branch_access_ids || [])])].filter(Boolean) : [] })}
                  className="mt-2 w-full rounded-lg border border-white/10 bg-[#0b0b0f] px-4 py-2 disabled:opacity-60">
                  <option value="assigned">Assigned home branch only</option>
                  <option value="selected">Selected branches</option>
                  <option value="all">All branches · including future branches</option>
                </select>
                <p className="mt-2 text-xs text-gray-400">Branch access does not grant new permissions. The role still controls what this employee can do. Only a super-admin can change this setting.</p>
                {form.branch_access === "selected" && <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {branchesRaw.map((branch) => {
                    const id = Number(branch.id);
                    const isHome = id === Number(form.branch_id);
                    const checked = isHome || (form.branch_access_ids || []).map(Number).includes(id);
                    return <label key={id} className="flex items-center gap-2 rounded-lg border border-white/10 p-3 text-sm">
                      <input type="checkbox" checked={checked} disabled={!canSetBranchAccess || isHome}
                        onChange={(e) => setForm((prev) => ({ ...prev, branch_access_ids: e.target.checked
                          ? [...new Set([...(prev.branch_access_ids || []), id])]
                          : (prev.branch_access_ids || []).filter((b) => Number(b) !== id) }))} />
                      {branch.name}{isHome && <span className="text-xs text-gray-500">Home</span>}
                    </label>;
                  })}
                </div>}
              </div>

              <div>
                <label className="text-xs text-gray-400 uppercase tracking-widest">
                  Billing POS Counter
                </label>

                <select
                  value={form.counter_id}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      counter_id: e.target.value ? Number(e.target.value) : "",
                    })
                  }
                  className="mt-1 w-full px-4 py-2 rounded-lg bg-black/40 border border-white/10"
                >
                  <option value="">No counter — normal staff access</option>

                  {counters.map((counter) => (
                    <option key={counter.id} value={counter.id}>
                      {counter.counter_name} ({counter.counter_code})
                    </option>
                  ))}
                </select>

                <p className="mt-1 text-xs text-gray-500">
                  Select a counter only for staff who should log in to Billing
                  POS.
                </p>
              </div>
            </div>

            <p className="text-xs text-gray-400 uppercase tracking-widest mb-3">
              Compensation
            </p>

            <div className="space-y-4">
              {/* SALARY */}
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.compensation.salaryEnabled}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      compensation: {
                        ...form.compensation,
                        salaryEnabled: e.target.checked,
                      },
                    })
                  }
                />
                Fixed Salary
              </label>

              {form.compensation.salaryEnabled && (
                <input
                  type="number"
                  placeholder="Monthly Salary (₹)"
                  value={form.compensation.salaryAmount}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      compensation: {
                        ...form.compensation,
                        salaryAmount: e.target.value,
                      },
                    })
                  }
                  className="px-4 py-2 rounded-lg bg-black/40 border border-white/10 w-full"
                />
              )}

              <label className="flex items-center gap-3 mt-4">
                <input
                  type="checkbox"
                  checked={form.compensation.commissionEnabled}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      compensation: {
                        ...form.compensation,
                        commissionEnabled: e.target.checked,
                      },
                    })
                  }
                />
                Commission
              </label>

              {form.compensation.commissionEnabled && (
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={form.compensation.commissionType}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        compensation: {
                          ...form.compensation,
                          commissionType: e.target.value,
                        },
                      })
                    }
                    className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="flat">Flat Amount (₹)</option>
                  </select>

                  <input
                    type="number"
                    placeholder={
                      form.compensation.commissionType === "percentage"
                        ? "Commission %"
                        : "Commission ₹"
                    }
                    value={form.compensation.commissionValue}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        compensation: {
                          ...form.compensation,
                          commissionValue: e.target.value,
                        },
                      })
                    }
                    className="px-4 py-2 rounded-lg bg-black/40 border border-white/10"
                  />
                </div>
              )}
            </div>

            <p className="text-xs text-gray-400 uppercase tracking-widest my-3">
              Permissions
            </p>

            <div className="max-h-[450px] overflow-y-auto rounded-xl border border-white/10 bg-black/20 p-4 space-y-6">
              {Object.entries(sortedGroupedPermissions).map(
                ([module, permissions]) => {
                  const ids = permissions.map((p) => p.id);

                  const allSelected = ids.every((id) =>
                    form.permissions.includes(id),
                  );

                  return (
                    <div
                      key={module}
                      className="rounded-xl border border-white/10 bg-white/5 p-4"
                    >
                      <div className="flex justify-between items-center mb-4 border-b border-white/10 pb-3">
                        <h3 className="text-lg font-semibold text-amber-400">
                          {module}
                        </h3>

                        <PermissionButton permissionModule="staff"
                          type="button"
                          onClick={() => {
                            if (allSelected) {
                              setForm((prev) => ({
                                ...prev,
                                permissions: prev.permissions.filter(
                                  (id) => !ids.includes(id),
                                ),
                              }));
                            } else {
                              setForm((prev) => ({
                                ...prev,
                                permissions: [
                                  ...new Set([...prev.permissions, ...ids]),
                                ],
                              }));
                            }
                          }}
                          className="text-xs px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20"
                        >
                          {allSelected ? "Deselect All" : "Select All"}
                        </PermissionButton>
                      </div>

                      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {permissions.map((perm) => (
                          <label
                            key={perm.id}
                            className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/30 hover:border-amber-400/40 hover:bg-white/5 p-3 cursor-pointer transition"
                          >
                            <input
                              type="checkbox"
                              checked={form.permissions.includes(perm.id)}
                              onChange={() => togglePermission(perm.id)}
                              className="mt-1 accent-amber-400"
                            />

                            <div className="flex-1">
                              <div className="font-medium text-white">
                                {perm.display_name}
                              </div>

                              <div className="text-[11px] text-gray-500">
                                {perm.permission_key}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                },
              )}
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <PermissionButton permissionModule="staff"
                onClick={() => setShowModal(false)}
                className="px-6 py-2 rounded-xl border border-white/20"
              >
                Cancel
              </PermissionButton>
              <PermissionButton permissionModule="staff"
                onClick={saveUser}
                type="button"
                className="
    px-6 py-2.5 rounded-xl
    bg-amber-400
    text-black text-sm font-medium
    shadow-[0_8px_30px_rgba(255,193,7,0.35)]
    hover:bg-amber-300
    hover:shadow-[0_12px_40px_rgba(255,193,7,0.5)]
    active:scale-[0.98]
    transition-all
  "
              >
                Create User
              </PermissionButton>
            </div>
          </Modal>
        )}

        {showRoleModal && (
          <Modal onClose={() => setShowRoleModal(false)}>
            <h2 className="text-xl font-serif mb-6">Create New Role</h2>

            <input
              placeholder="Role Name (e.g. Store Manager)"
              value={newRole.name}
              onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
              className="w-full mb-4 px-4 py-2 rounded-lg bg-black/40 border border-white/10"
            />

            <input
              placeholder="Short Description"
              value={newRole.description}
              onChange={(e) =>
                setNewRole({ ...newRole, description: e.target.value })
              }
              className="w-full mb-6 px-4 py-2 rounded-lg bg-black/40 border border-white/10"
            />

            <div className="flex justify-between items-center mb-2">
              <p className="text-xs text-gray-400 uppercase tracking-widest">
                Default Permissions
              </p>

              <PermissionButton permissionModule="staff"
                type="button"
                onClick={() => {
                  if (isAllSelected) {
                    setNewRole((prev) => ({ ...prev, permissions: [] }));
                  } else {
                    const allIds = permissionsList.map((p) => p.id);
                    setNewRole((prev) => ({ ...prev, permissions: allIds }));
                  }
                }}
                className="text-xs px-3 py-1 rounded-md bg-white/10 hover:bg-white/20 transition"
              >
                {isAllSelected
                  ? "Deselect All"
                  : isPartial
                    ? "Select All (Partial)"
                    : "Select All"}
              </PermissionButton>
            </div>

            <div className="space-y-5 max-h-[450px] overflow-y-auto pr-2">
              {Object.entries(groupedPermissions).map(
                ([module, permissions]) => (
                  <div
                    key={module}
                    className="rounded-xl border border-white/10 bg-white/5 p-4"
                  >
                    <div className="flex items-center justify-between mb-3 border-b border-white/10 pb-2">
                      <h3 className="font-semibold text-amber-400">{module}</h3>

                      <PermissionButton permissionModule="staff"
                        type="button"
                        onClick={() => {
                          const ids = permissions.map((p) => p.id);

                          const allSelected = ids.every((id) =>
                            newRole.permissions.includes(id),
                          );

                          if (allSelected) {
                            setNewRole((prev) => ({
                              ...prev,
                              permissions: prev.permissions.filter(
                                (id) => !ids.includes(id),
                              ),
                            }));
                          } else {
                            setNewRole((prev) => ({
                              ...prev,
                              permissions: [
                                ...new Set([...prev.permissions, ...ids]),
                              ],
                            }));
                          }
                        }}
                        className="text-xs px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20"
                      >
                        {permissions.every((p) =>
                          newRole.permissions.includes(p.id),
                        )
                          ? "Deselect"
                          : "Select All"}
                      </PermissionButton>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {permissions.map((perm) => (
                        <label
                          key={perm.id}
                          className="flex items-center gap-3 rounded-lg p-2 hover:bg-white/5 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            className="accent-amber-400"
                            checked={newRole.permissions.includes(perm.id)}
                            onChange={() =>
                              setNewRole((prev) => ({
                                ...prev,
                                permissions: prev.permissions.includes(perm.id)
                                  ? prev.permissions.filter(
                                      (id) => id !== perm.id,
                                    )
                                  : [...prev.permissions, perm.id],
                              }))
                            }
                          />

                          <div>
                            <p className="text-sm font-medium">
                              {perm.display_name}
                            </p>

                            <p className="text-[11px] text-gray-500">
                              {perm.permission_key}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ),
              )}
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <PermissionButton permissionModule="staff"
                onClick={() => setShowRoleModal(false)}
                className="px-6 py-2 rounded-xl border border-white/20"
              >
                Cancel
              </PermissionButton>
              <PermissionButton permissionModule="staff"
                onClick={async () => {
                  if (!newRole.name) return;

                  const res = await fetch("/api/roles", {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                      name: newRole.name,
                      description: newRole.description,
                      permissions: newRole.permissions,
                    }),
                  });

                  const data = await res.json();

                  if (data.status === "success") {
                    const r = await fetch("/api/roles").then(
                      (r) => r.json(),
                    );

                    setRolesRaw(r.data || []); // ✅ IMPORTANT
                    setRoleList((r.data || []).map((x) => x.name));

                    setForm((prev) => ({
                      ...prev,
                      role_id: r.data?.[r.data.length - 1]?.id || "",
                      permissions:
                        r.data?.[r.data.length - 1]?.permissions || [],
                    }));

                    setNewRole({ name: "", description: "", permissions: [] });
                    setShowRoleModal(false);
                  }
                }}
                className="px-6 py-2 rounded-xl bg-amber-400 text-black font-medium"
              >
                Create Role
              </PermissionButton>
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-5">
      <div
        className="
          relative
          w-full
          max-w-6xl
          max-h-[92vh]
          overflow-y-auto
          rounded-2xl
          bg-[#0b0b0f]
          border
          border-white/10
          p-8
        "
      >
        <PermissionButton permissionModule="staff"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          ✕
        </PermissionButton>
        {children}
      </div>
    </div>
  );
}
